import React, { Dispatch } from 'react';
import {
  Button,
  Table,
  TableRow,
  TableBody,
  TableHeader,
  TableHeaderCell,
  Box,
  TableCell,
  Text,
  TrashIcon,
  SelectInput,
  Dropdown,
  DropdownOverlay,
  ActionList,
  ActionListItem,
  TextInput,
  LockIcon,
  Link,
  PlusIcon,
} from '@razorpay/blade/components';
import { EarningsComponentType } from 'components/SalaryComponents/types';
import { nonDeletablePredfinedComponentTypes } from 'components/SalaryStructure/constants';
import { CustomSalaryComponent, CustomSalaryComponentsAction } from './types';
import {
  getComponentDescription,
  getNewComponent,
  sortComponentsListByComponentType,
} from './utils';
import { INTEGER_AMOUNT } from 'utils/regexes';
import { toIndianCurrencyString } from 'utils/Numbers';

export const CustomSalaryComponents = ({
  selectedComponents,
  dispatch,
  isSubmitting,
  allComponents,
  isFetchingComponents,
}: {
  selectedComponents: CustomSalaryComponent[];
  dispatch: Dispatch<CustomSalaryComponentsAction>;
  isSubmitting: boolean;
  allComponents: EarningsComponentType[];
  isFetchingComponents: boolean;
}) => {
  const alreadyAddedComponentIds = selectedComponents
    .filter((c) => c.componentId)
    .map((c) => c.componentId);

  const onAddNewComponent = () => {
    const newComponent = getNewComponent();
    dispatch({ type: 'COMPONENT_ADDED', payload: { component: newComponent } });
  };

  const onDeleteComponent = (id: string) => {
    dispatch({ type: 'COMPONENT_DELETED', payload: { id } });
  };

  const onComponentAmountChange = (id: string, amount: string) => {
    dispatch({
      type: 'COMPONENT_AMOUNT_CHANGED',
      payload: { id, amount },
    });
  };

  const onUpdateComponent = (id: string, updates: Partial<CustomSalaryComponent>) => {
    dispatch({
      type: 'COMPONENT_UPDATED',
      payload: { id, updates },
    });
  };

  const sortedComponents = sortComponentsListByComponentType({ components: selectedComponents });

  return (
    <Box marginTop="spacing.5">
      <Table data={{ nodes: sortedComponents }} gridTemplateColumns="1fr 1fr 52px">
        {(items) => (
          <>
            <TableHeader>
              <TableHeaderCell>Component</TableHeaderCell>
              <TableHeaderCell>Monthly Amount</TableHeaderCell>
              <TableHeaderCell> </TableHeaderCell>
            </TableHeader>
            <TableBody>
              {items.map((component) => {
                const isComponentEditable = !component.isPredefined;
                const isComponentDeletable = !nonDeletablePredfinedComponentTypes.includes(
                  component.componentType || 'COMPONENT_TYPE_NA',
                );

                return (
                  <TableRow item={component} key={component.id}>
                    {isComponentEditable ? (
                      <TableCell>
                        <Box width="100%">
                          <Dropdown selectionType="single">
                            <SelectInput
                              accessibilityLabel="Select Component"
                              placeholder="Select Component"
                              name={`component-${component.id}`}
                              value={component.componentId || ''}
                              isDisabled={isFetchingComponents || isSubmitting}
                              onChange={({ values }) => {
                                const selectedId = values[0];
                                const selectedComponent = allComponents.find(
                                  (c) => c.id === selectedId,
                                );

                                if (selectedComponent) {
                                  onUpdateComponent(component.id, {
                                    componentId: selectedComponent.id,
                                    name: selectedComponent.name,
                                    category: selectedComponent.category,
                                  });
                                }
                              }}
                            />
                            <DropdownOverlay>
                              <ActionList>
                                {allComponents.map((cmp) => (
                                  <ActionListItem
                                    isSelected={cmp.id === component.componentId}
                                    key={cmp.id}
                                    title={cmp.name}
                                    description={getComponentDescription(cmp)}
                                    value={cmp.id}
                                    isDisabled={
                                      component.componentId !== cmp.id &&
                                      alreadyAddedComponentIds.includes(cmp.id)
                                    }
                                  />
                                ))}
                              </ActionList>
                            </DropdownOverlay>
                          </Dropdown>
                        </Box>
                      </TableCell>
                    ) : (
                      <TableCell>
                        <Text marginLeft="spacing.2">{component.name}</Text>
                      </TableCell>
                    )}
                    <TableCell>
                      {component.componentType === 'COMPONENT_TYPE_SPECIAL_ALLOWANCE' ? (
                        <Text size="medium" color="surface.text.gray.subtle">
                          Residual component
                        </Text>
                      ) : (
                        <TextInput
                          placeholder="Enter Amount"
                          accessibilityLabel={`${component.name || 'Component'} amount`}
                          value={toIndianCurrencyString(
                            component.amount ? Number(component.amount) : 0,
                            true,
                          )}
                          isDisabled={isSubmitting}
                          onChange={({ value }) => {
                            if (INTEGER_AMOUNT.test(value || '')) {
                              const numValue = Number(value?.replace(/,/g, '')) ?? 0;
                              onComponentAmountChange(component.id, numValue.toString());
                            }
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {isComponentDeletable ? (
                        <Button
                          isDisabled={isSubmitting}
                          onClick={() => onDeleteComponent(component.id)}
                          icon={TrashIcon}
                          variant="tertiary"
                          size="xsmall"
                          accessibilityLabel="delete component"
                        />
                      ) : (
                        <LockIcon margin="spacing.2" color="surface.icon.gray.disabled" />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </>
        )}
      </Table>
      <Box marginTop="spacing.5">
        <Link
          isDisabled={isSubmitting}
          size="small"
          variant="button"
          icon={PlusIcon}
          iconPosition="left"
          onClick={() => onAddNewComponent()}>
          Add Component
        </Link>
      </Box>
    </Box>
  );
};
